import styled from "@emotion/styled";
import { FC, useState } from "react";
import { FieldInputProps, useForm } from "react-final-form";

import { Edit } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken } from "@livechat/design-system-react-components";

import {
  IRuleActionAddFollowers,
  IRuleActionAddTags,
  IRuleActionAssign,
  IRuleActionMessage,
  IRuleActionPriority,
  IRuleActionRemoveFollowers,
  IRuleActionRemoveTags,
  IRuleActionSendFollowUp,
  IRuleActionSendRatingRequest,
  IRuleActionStatus,
} from "store/entities/rules/interfaces";

import { ChooseActionOrTriggerModal } from "../components/ChooseActionOrTriggerModal";
import { ACTION_NAME } from "../components/constants";

import { ActionFieldArrayElement } from "./interfaces";
import { Assignment } from "./parts/Assignment";
import { Followers } from "./parts/Followers";
import { Message } from "./parts/Message";
import { Priority } from "./parts/Priority";
import { Status } from "./parts/Status";
import { Tag } from "./parts/Tag";
import { ActionType } from "./types";
import { changeAction, defaultActions, isActionType, mapActionToActionType } from "./utils";

interface IProps {
  input: FieldInputProps<ActionFieldArrayElement>;
}

const ActionElement: FC<IProps> = ({ input }) => {
  if (!input.value?.type) {
    return null;
  }

  if (isActionType(input, "spam")) {
    return null;
  }

  if (isActionType<IRuleActionAssign>(input, "assign")) {
    return <Assignment input={input} />;
  }

  if (
    isActionType<IRuleActionSendRatingRequest>(input, "sendRatingRequest") ||
    isActionType<IRuleActionSendFollowUp>(input, "sendFollowUp") ||
    isActionType<IRuleActionMessage>(input, "message")
  ) {
    return <Message input={input} />;
  }

  if (isActionType<IRuleActionStatus>(input, "status")) {
    return <Status input={input} />;
  }

  if (isActionType<IRuleActionPriority>(input, "priority")) {
    return <Priority input={input} />;
  }

  if (isActionType<IRuleActionAddTags>(input, "addTags") || isActionType<IRuleActionRemoveTags>(input, "removeTags")) {
    return <Tag input={input} />;
  }

  if (
    isActionType<IRuleActionAddFollowers>(input, "addFollowers") ||
    isActionType<IRuleActionRemoveFollowers>(input, "removeFollowers")
  ) {
    return <Followers input={input} />;
  }
};

interface IActionProps {
  input: FieldInputProps<ActionFieldArrayElement>;
  limitReached: boolean;
}

export const Action: FC<IActionProps> = ({ input, limitReached }) => {
  const form = useForm();

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleSelectAction = (type: ActionType): void => {
    form.resetFieldState(input.name);
    changeAction(input, defaultActions[type]);
    setEditModalOpen(false);
  };

  const actionType = mapActionToActionType(input.value);

  const actionElement = ActionElement({ input });

  return (
    <>
      <Button
        kind="secondary"
        icon={<Icon source={Edit} />}
        iconPosition="right"
        onClick={() => setEditModalOpen(true)}
        disabled={limitReached}
      >
        {actionType && ACTION_NAME[actionType]}
      </Button>
      {actionElement && <ActionContainer>{actionElement}</ActionContainer>}
      <ChooseActionOrTriggerModal
        type="action"
        mode="edit"
        selectedAction={actionType}
        isVisible={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSelect={handleSelectAction}
      />
    </>
  );
};

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${SpacingToken.Spacing4});
  padding-left: var(${SpacingToken.Spacing6});
  border-left: 1px dashed var(${DesignToken.BorderBasicPrimary});
  margin-top: var(${SpacingToken.Spacing4});
`;
